/*
 * Cart I/O + formatting helpers for <wholesale-order> (assets/wholesale-order.js).
 * Split out to keep both files under the 300-line cap. Nothing here writes to the list's DOM;
 * renderCartSections() updates the theme's cart drawer / header bubble.
 * Must load before wholesale-order.js (both are `defer`, so document order is execution order).
 */
'use strict';

window.WholesaleCart = {
  // Adds one product's lines. Throws an Error carrying `shopperMessage` (Shopify's 422 reason,
  // e.g. "You can only add 5 of ...") and `context` for logging.
  async add(entries, drawer, fallbackMessage) {
    const body = JSON.stringify({
      items: entries.map(([id, item]) => ({ id: Number(id), quantity: item.qty })),
      sections: drawer ? ['cart-drawer', 'cart-icon-bubble'] : ['cart-icon-bubble'],
      sections_url: window.location.pathname,
    });
    const response = await fetch(routes.cart_add_url, { ...fetchConfig(), body });
    const data = await response.json();
    if (!response.ok || data.status) {
      const err = new Error(data.description || data.message || fallbackMessage);
      err.shopperMessage = err.message;
      err.context = { status: response.status, items: entries.map(([id, item]) => ({ id, qty: item.qty })) };
      throw err;
    }
    return data;
  },

  async fetchCart() {
    const response = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`GET ${routes.cart_url}.js returned ${response.status}`);
    return response.json();
  },

  // Sums quantity + final_line_price per variant and per product. A variant can span several
  // lines (different line-item properties / selling plans), so never assume one line per variant.
  summarise(cart) {
    const byVariant = new Map();
    const byProduct = new Map();
    const bump = (map, key, item) => {
      const current = map.get(key) || { qty: 0, value: 0 };
      map.set(key, { qty: current.qty + item.quantity, value: current.value + item.final_line_price });
    };
    cart.items.forEach((item) => {
      bump(byVariant, String(item.variant_id), item);
      bump(byProduct, String(item.product_id), item);
    });
    return { byVariant, byProduct };
  },

  // Same section swap CartDrawer.renderContents() does, minus opening the drawer — popping a
  // drawer after every product would make bulk entry miserable.
  renderCartSections(sections, drawer) {
    if (!sections) return;
    const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
    if (drawer && sections['cart-drawer']) {
      const source = parse(sections['cart-drawer']).querySelector('#CartDrawer');
      const target = drawer.querySelector('#CartDrawer');
      if (source && target) target.innerHTML = source.innerHTML;
      drawer.classList.remove('is-empty');
    }
    const bubble = document.getElementById('cart-icon-bubble');
    const bubbleSource = sections['cart-icon-bubble'] && parse(sections['cart-icon-bubble']).querySelector('.shopify-section');
    if (bubble && bubbleSource) bubble.innerHTML = bubbleSource.innerHTML;
    if (typeof freeShippMsg === 'function') freeShippMsg();
    if (typeof cartTerms === 'function') cartTerms();
  },

  // B2B volume pricing: Shopify prices the whole line by the tier its TOTAL cart quantity
  // reaches, so the estimate uses (already in cart + adding). An estimate only; the in-cart
  // columns always come from /cart.js. Without data-breaks this is just the variant price.
  unitPrice(row, qty) {
    let price = Number(row.dataset.price);
    if (!row.dataset.breaks) return price;
    const total = Number(row.dataset.inCart) + qty;
    JSON.parse(row.dataset.breaks).forEach((tier) => {
      if (total >= tier.min && tier.price < price) price = tier.price;
    });
    return price;
  },

  // Client-side mirror of the quantity rules rendered into each row. Called at submit time
  // (inside the add queue) so data-in-cart reflects any add that finished just before. Rows not
  // on screen (other filter/page) can't be checked; Shopify's own validation is the backstop.
  validate(entries, rowFor, strings) {
    const errors = [];
    entries.forEach(([id, item]) => {
      const row = rowFor(id);
      if (!row) return;
      const inCart = Number(row.dataset.inCart);
      const step = Number(row.dataset.step) || 1;
      const min = Number(row.dataset.min) || 1;
      if (item.qty % step !== 0) {
        errors.push(strings.errorStep.replace('[item]', item.title).replace('[step]', step));
      } else if (inCart + item.qty < min) {
        errors.push(strings.errorMin.replace('[item]', item.title).replace('[min]', min));
      } else if (row.dataset.limit !== undefined && inCart + item.qty > Number(row.dataset.limit)) {
        const left = Math.max(0, Number(row.dataset.limit) - inCart);
        errors.push(strings.errorMax.replace('[item]', item.title).replace('[max]', left));
      }
    });
    return errors;
  },

  // pending Map -> [[productId, [[variantId, item], ...]], ...]
  groupByProduct(pending) {
    const groups = new Map();
    pending.forEach((item, id) => {
      if (!groups.has(item.productId)) groups.set(item.productId, []);
      groups.get(item.productId).push([id, item]);
    });
    return [...groups];
  },

  pluralise(count, strings) {
    return (count === 1 ? strings.itemOne : strings.itemOther).replace('[count]', count);
  },

  money(cents) {
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(Math.round(cents), theme.moneyFormat);
    }
    console.error('[wholesale-order] theme.Currency.formatMoney is unavailable; showing an unformatted amount.');
    return (cents / 100).toFixed(2);
  },

  // Multi-currency (theme script mode) converts span.money text from shop currency in place;
  // anything we re-render must go back through it or it reverts to ZAR.
  convertCurrency(nodes) {
    if (theme.mlcurrency && typeof currenciesChange === 'function') currenciesChange(nodes);
  },
};
